import { medicationInfoService } from '../lib/medicationInfoService';
import medicationsData from './medications.json';

// Rozszerzone dane z medications.json
export interface MedicationInfoExtended {
  name: string;
  unit: string;
  activeSubstance?: string;
  category?: 'LEK' | 'SUPLEMENT';
  type?: 'permanent' | 'temporary' | 'tapering' | 'one-time';
  takingInstructions?: 'with_meal' | 'before_meal' | 'after_meal' | 'empty_stomach' | null;
  isCorticosteroid?: boolean;
  defaultFrequency?: string;
  defaultDosage?: number;
}

// Stary interfejs dla kompatybilności wstecznej
export interface MedicationInfo {
  name: string;
  unit: string;
}

// Mapowanie jednostek (fallback gdy brak w JSON)
const UNIT_FALLBACK: Record<string, string> = {
  'Mesalazyna (Pentasa) 500mg': 'tabletka', 'Mesalazyna (Pentasa) 1000mg': 'tabletka',
  'Mesalazyna (Asacol) 400mg': 'tabletka', 'Mesalazyna (Asacol) 800mg': 'tabletka',
  'Mesalazyna (Salofalk) 250mg': 'tabletka', 'Mesalazyna (Salofalk) 500mg': 'tabletka',
  'Mesalazyna (Salaza) 500mg': 'tabletka', 'Mesalazyna (Mezavant) 1200mg': 'tabletka',
  'Mesalazyna (czopki) 250mg': 'czopek', 'Mesalazyna (czopki) 500mg': 'czopek', 'Mesalazyna (czopki) 1000mg': 'czopek',
  'Mesalazyna (wlewki) 1g': 'wlewka', 'Mesalazyna (wlewki) 2g': 'wlewka', 'Mesalazyna (wlewki) 4g': 'wlewka',
  'Sulfasalazyna 500mg': 'tabletka', 'Sulfasalazyna 1000mg': 'tabletka',
  'Prednizon 5mg': 'tabletka', 'Prednizon 10mg': 'tabletka', 'Prednizon 20mg': 'tabletka', 'Prednizon 30mg': 'tabletka',
  'Prednizolon 5mg': 'tabletka', 'Prednizolon 10mg': 'tabletka', 'Prednizolon 20mg': 'tabletka',
  'Budesonid (Entocort) 3mg': 'kapsułka', 'Budesonid (Entocort) 6mg': 'kapsułka',
  'Budesonid (Budenofalk) 3mg': 'kapsułka', 'Budesonid (Budenofalk) 6mg': 'kapsułka',
  'Rifaxymina (Xifaxan) 200mg': 'tabletka', 'Rifaxymina (Xifaxan) 400mg': 'tabletka', 'Rifaxymina (Xifaxan) 550mg': 'tabletka',
  'Azatiopryna (Imuran) 25mg': 'tabletka', 'Azatiopryna (Imuran) 50mg': 'tabletka', 'Azatiopryna (Imuran) 75mg': 'tabletka', 'Azatiopryna (Imuran) 100mg': 'tabletka',
  'Metotreksat 2.5mg': 'tabletka', 'Metotreksat 5mg': 'tabletka', 'Metotreksat 10mg': 'tabletka', 'Metotreksat 15mg': 'tabletka',
  'Infliksymab (Remicade) 100mg': 'wlew dożylny', 'Adalimumab (Humira) 40mg': 'wstrzyknięcie',
  'Ustekinumab (Stelara) 45mg': 'wstrzyknięcie', 'Ustekinumab (Stelara) 90mg': 'wstrzyknięcie',
  'Wedolizumab (Entyvio) 300mg': 'wlew dożylny', 'Tofacitinib (Xeljanz) 5mg': 'tabletka', 'Tofacitinib (Xeljanz) 10mg': 'tabletka',
  'Fosforan kodeiny 15mg': 'tabletka', 'Fosforan kodeiny 30mg': 'tabletka',
  'Loperamid (Imodium) 2mg': 'tabletka', 'Loperamid (Imodium) 4mg': 'tabletka',
  'CDS22-formula (dawniej Vivomixx) 450 mld': 'saszetka', 'CDS22-formula (dawniej Vivomixx) 225 mld': 'saszetka',
  'CDS22-formula (dawniej Vivomixx) 112 mld': 'kapsułka', 'CDS22-formula (dawniej Vivomixx) 5 mld': 'saszetka',
  'CDS22-formula (dawniej Vivomixx) 10 mld': 'kapsułka', 'Vivomixx (stara nazwa) 450 mld': 'saszetka',
  'Bifidobacterium': 'kapsułka', 'Lactobacillus': 'kapsułka',
  'Saccharomyces boulardii 250mg': 'kapsułka', 'Saccharomyces boulardii 500mg': 'kapsułka',
  'Witamina D 1000 IU': 'kapsułka', 'Witamina D 2000 IU': 'kapsułka', 'Witamina D 4000 IU': 'kapsułka', 'Witamina D 5000 IU': 'kapsułka',
  'Witamina D + K2 1000 IU': 'kapsułka', 'Witamina D + K2 2000 IU': 'kapsułka', 'Witamina D + K2 4000 IU': 'kapsułka',
  'Witamina B12 1000mcg': 'tabletka', 'Witamina B12 2000mcg': 'tabletka', 'Witamina B12 5000mcg': 'tabletka',
  'Witamina B kompleks': 'tabletka',
  'Kwas foliowy 0.4mg': 'tabletka', 'Kwas foliowy 0.8mg': 'tabletka', 'Kwas foliowy 1mg': 'tabletka', 'Kwas foliowy 5mg': 'tabletka',
  'Kwas foliowy + B12': 'tabletka', 'Kwas foliowy + żelazo': 'tabletka',
  'Żelazo 50mg': 'tabletka', 'Żelazo 100mg': 'tabletka', 'Żelazo 200mg': 'tabletka',
  'Potas 200mg': 'tabletka', 'Potas 400mg': 'tabletka', 'Potas 600mg': 'tabletka',
  'Magnez 100mg': 'tabletka', 'Magnez 200mg': 'tabletka', 'Magnez 300mg': 'tabletka', 'Magnez 400mg': 'tabletka',
  'Wapń 500mg': 'tabletka', 'Wapń 1000mg': 'tabletka', 'Wapń 1500mg': 'tabletka',
  'Cynk 5mg': 'tabletka', 'Cynk 10mg': 'tabletka', 'Cynk 15mg': 'tabletka', 'Cynk 20mg': 'tabletka',
  'Selen 50mcg': 'tabletka', 'Selen 100mcg': 'tabletka', 'Selen 200mcg': 'tabletka',
  'Multiwitamina': 'tabletka',
  'Omega-3 500mg': 'kapsułka', 'Omega-3 1000mg': 'kapsułka', 'Omega-3 2000mg': 'kapsułka',
  'Kurkumina 500mg': 'kapsułka', 'Kurkumina 1000mg': 'kapsułka',
  'Boswellia 300mg': 'kapsułka', 'Boswellia 500mg': 'kapsułka',
  'Aloes 50ml': 'ml', 'Aloes 100ml': 'ml',
  'Olej z wiesiołka 500mg': 'kapsułka', 'Olej z wiesiołka 1000mg': 'kapsułka',
  'Olej z ryb 500mg': 'kapsułka', 'Olej z ryb 1000mg': 'kapsułka',
  'Kolagen 5g': 'saszetka', 'Kolagen 10g': 'saszetka',
  'Glutamina 5g': 'g', 'Glutamina 10g': 'g', 'Glutamina 15g': 'g'
};

const MEDICATIONS_FROM_JSON: MedicationInfoExtended[] = (medicationsData as any[]).map((m: any) => ({
  ...m,
  unit: UNIT_FALLBACK[m.name] || 'sztuka'
}));

let cachedMedications: MedicationInfoExtended[] | null = null;
let medicationsLoadPromise: Promise<MedicationInfoExtended[]> | null = null;

export const loadMedications = async (): Promise<MedicationInfoExtended[]> => {
  if (cachedMedications) {
    return cachedMedications;
  }

  if (medicationsLoadPromise) {
    return medicationsLoadPromise;
  }

  medicationsLoadPromise = (async () => {
    try {
      const dbMedications = await medicationInfoService.getAll();
      const staticMap = new Map(MEDICATIONS_FROM_JSON.map(m => [m.name.toLowerCase(), m]));
      const combined: MedicationInfoExtended[] = [...MEDICATIONS_FROM_JSON];
      dbMedications.forEach(dbMed => {
        const ext: MedicationInfoExtended = {
          name: dbMed.name,
          unit: (dbMed as any).unit || 'sztuka',
          ...(staticMap.get(dbMed.name.toLowerCase()) || {})
        };
        if (!staticMap.has(dbMed.name.toLowerCase())) {
          combined.push(ext);
        }
      });
      cachedMedications = combined;
      return combined;
    } catch (error) {
      console.error('Błąd ładowania leków z bazy, używam danych z JSON:', error);
      cachedMedications = MEDICATIONS_FROM_JSON;
      return MEDICATIONS_FROM_JSON;
    } finally {
      medicationsLoadPromise = null;
    }
  })();

  return medicationsLoadPromise;
};

export const refreshMedications = async (): Promise<MedicationInfoExtended[]> => {
  cachedMedications = null;
  medicationsLoadPromise = null;
  return loadMedications();
};

export const getWZJGMedications = (): MedicationInfoExtended[] => {
  return cachedMedications || MEDICATIONS_FROM_JSON;
};

export const STATIC_MEDICATIONS = MEDICATIONS_FROM_JSON as MedicationInfo[];
export const WZJG_MEDICATIONS = MEDICATIONS_FROM_JSON as MedicationInfo[];

export const FREQUENCY_OPTIONS = [
  '1x dziennie',
  '2x dziennie',
  '3x dziennie',
  '4x dziennie',
  'inne'
];

export const filterMedications = (searchTerm: string): MedicationInfoExtended[] => {
  const medications = getWZJGMedications();
  if (!searchTerm.trim()) {
    return medications;
  }
  const lowerSearch = searchTerm.toLowerCase();
  return medications.filter(med =>
    med.name.toLowerCase().includes(lowerSearch)
  );
};

export const getMedicationInfo = (name: string): MedicationInfoExtended | undefined => {
  return getWZJGMedications().find(med => med.name === name);
};

export const getUnitForMedication = (name: string): string => {
  const med = getMedicationInfo(name);
  return med ? med.unit : 'sztuka';
};
